import { useEffect, useContext, useCallback, useRef } from 'react';

import { LeafletMouseEvent } from 'leaflet';

import { getParcelDetail } from '@api/atlas';

import { sendGAEvent } from '@utils/google-analytics';

import { MIN_ZOOM_LEVEL_FOR_CURSOR_WITH_TEXT } from '@atlas/molecules/map-cursor-with-text';
import { SearchContext } from '@atlas/contexts/search';

import { LatLngTuple } from '@typings/geo';

// ga events for zoom in & zoom out buttons in map
export const useGaEvents = () => {
  const zoomInElem = document.getElementsByClassName(
    'leaflet-control-zoom-in',
  )[0] as HTMLElement;

  const zoomOutElem = document.getElementsByClassName(
    'leaflet-control-zoom-out',
  )[0] as HTMLElement;

  useEffect(() => {
    if (!zoomInElem || !zoomOutElem) return;
    const zoomInListener = () => {
      sendGAEvent('atlas_map', 'click', 'zoomin');
    };

    const zoomOutListener = () => {
      sendGAEvent('atlas_map', 'click', 'zoomout');
    };

    zoomInElem.addEventListener('click', zoomInListener, false);
    zoomOutElem.addEventListener('click', zoomOutListener, false);

    return () => {
      zoomInElem.removeEventListener('click', zoomInListener);
      zoomOutElem.removeEventListener('click', zoomOutListener);
    };
  }, [zoomInElem, zoomOutElem]);
};

const DOUBLE_CLICK_PREVENT_DELAY = 400;

export const useOnClickDetailOpen = (zoom: number) => {
  const { setSearch } = useContext(SearchContext);

  const prevent = useRef(false);
  const timer = useRef<NodeJS.Timeout>();

  const onMapClick = useCallback(
    ({ latlng }: LeafletMouseEvent) => {
      if (zoom < MIN_ZOOM_LEVEL_FOR_CURSOR_WITH_TEXT) return;

      timer.current = setTimeout(async () => {
        if (prevent.current) {
          prevent.current = false;
          return;
        }

        const point: LatLngTuple = [latlng.lng, latlng.lat];
        const parcel = await getParcelDetail(point);

        if (parcel.relatedBuildings.length === 1) {
          setSearch({
            address: parcel.relatedBuildings[0].address,
            text: '',
          });
        } else {
          setSearch({
            text: '',
            point: point.reverse() as LatLngTuple,
            parcel: parcel._id,
          });
        }
      }, DOUBLE_CLICK_PREVENT_DELAY);
    },
    [setSearch, zoom],
  );

  const onMapDoubleClick = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    prevent.current = true;
  }, []);

  return { onMapClick, onMapDoubleClick };
};
